import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import SiteDashboard from './components/SiteDashboard';
import SiteDetails from './components/SiteDetails';
import SeoTab from './components/SeoTab';
import AddWebsite from './components/AddWebsite';
import Reports from './components/Reports';
import Profile from './components/Profile';
import EditProfile from './components/EditProfile';
import MyWebsites from './components/MyWebsites';
import Settings from './components/Settings';
import EmailRegistration from './components/EmailRegistration';
import UserManagement from './components/UserManagement';
import Login from './components/Login';
import Register from './components/Register';
import ProtectedRoute from './components/ProtectedRoute';
import BackupRestore from './components/BackupRestore';
import SiteMonitor from './components/SiteMonitor';
import SiteMonitoring from './components/SiteMonitoring';
import SecurityMonitoring from './components/SecurityMonitoring';
import AllPlugins from './components/AllPlugins';
import InviteHandler from './components/InviteHandler';
import PublicAccessHandler from './components/PublicAccessHandler';
import ActivityLog from './components/ActivityLog';
import './styles/themes.css';
import './styles/syntax-highlighting.css';

const FULL_ACCESS_ROLES = ['admin', 'manager', 'technician'];
const VIEWER_ROLES = ['viewer', 'user'];

const App = () => {
  return (
    <ThemeProvider>
      <Router>
        <div className="App">
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/invite" element={<InviteHandler />} />
          <Route path="/public/:token" element={<PublicAccessHandler />} />

          {/* Root: redirect to /login if not logged in, /dashboard if logged in */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <SiteDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sites/:siteId"
            element={
              <ProtectedRoute allowedRoles={[...FULL_ACCESS_ROLES, ...VIEWER_ROLES]}>
                <SiteDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sites/:siteId/plugins"
            element={
              <ProtectedRoute allowedRoles={[...FULL_ACCESS_ROLES, ...VIEWER_ROLES]}>
                <SiteDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/seo"
            element={
              <ProtectedRoute allowedRoles={FULL_ACCESS_ROLES}>
                <SeoTab />
              </ProtectedRoute>
            }
          />
          <Route
            path="/add-website"
            element={
              <ProtectedRoute allowedRoles={FULL_ACCESS_ROLES}>
                <AddWebsite />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-websites"
            element={
              <ProtectedRoute allowedRoles={[...FULL_ACCESS_ROLES, ...VIEWER_ROLES]}>
                <MyWebsites />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute allowedRoles={[...FULL_ACCESS_ROLES, ...VIEWER_ROLES]}>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/monitoring"
            element={
              <ProtectedRoute allowedRoles={[...FULL_ACCESS_ROLES, ...VIEWER_ROLES]}>
                <SiteMonitoring />
              </ProtectedRoute>
            }
          />
          <Route
            path="/security"
            element={
              <ProtectedRoute allowedRoles={[...FULL_ACCESS_ROLES, ...VIEWER_ROLES]}>
                <SecurityMonitoring />
              </ProtectedRoute>
            }
          />
          <Route
            path="/plugins"
            element={
              <ProtectedRoute allowedRoles={[...FULL_ACCESS_ROLES, ...VIEWER_ROLES]}>
                <AllPlugins />
              </ProtectedRoute>
            }
          />
          <Route
            path="/activity-log"
            element={
              <ProtectedRoute allowedRoles={[...FULL_ACCESS_ROLES, ...VIEWER_ROLES]}>
                <ActivityLog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute allowedRoles={FULL_ACCESS_ROLES}>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/backup"
            element={
              <ProtectedRoute allowedRoles={FULL_ACCESS_ROLES}>
                <BackupRestore />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sites/:siteId/monitor"
            element={
              <ProtectedRoute allowedRoles={FULL_ACCESS_ROLES}>
                <SiteMonitor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute allowedRoles={FULL_ACCESS_ROLES}>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/edit"
            element={
              <ProtectedRoute>
                <EditProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email-registration"
            element={
              <ProtectedRoute allowedRoles={FULL_ACCESS_ROLES}>
                <EmailRegistration />
              </ProtectedRoute>
            }
          />
          {/* Unknown routes: redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </div>
      </Router>
    </ThemeProvider>
  );
};

export default App;